import { InsightRendererProps, Proposition } from '@ecosystem/newron-client-collections';
import { ReactNode } from 'react';

import { ErrorInfo } from '../data/types';
import { DataOptions, Entity, HTTPMethod, Params } from '../types';

export type PageSettings = Record<string, Params>;

export type PageInfo = {
  name: string;
  url: string;
  query?: Record<string, string[]>;
  appVersion?: string;
  syncSettings?: SyncSettings;
};

export type SyncSettings = {
  fromPage: string;
  components: SyncComponent[];
};

export type SyncComponent = {
  from: {
    name: string;
    settings: string[];
  };
  to: {
    name: string;
    settings: string[];
  };
};

export type DataInfo = {
  url: string;
  requestMethod?: HTTPMethod;
};

export type ContextUpdates = Record<string, Entity[]>;

/**
 * upsellQualtricsURL: The Qualtrics URL to trigger the upsell survey.
 * upsellAnalyticsURL: The URL to send upsell analytics data to record user impression of Upsells.
 */
export type UpsellContext = {
  upsellQualtricsURL: string;
  upsellAnalyticsURL: string;
};

export enum PageType {
  scorecard = 'scorecard',
  time = 'timeSeries',
  segments = 'segmentation',
}

export enum SettingsDefinitionType {
  openMarkets = 'openMarkets',
  focusPeriod = 'focusPeriod',
  change = 'change',
  currencyCode = 'currency_code',
}

export type CorePageProviderProps = {
  children: ReactNode;
  context: {
    name: string;
    url: string;
    excludeFilters?: boolean;
    excludePriceFilter?: boolean;
  };
  page: PageInfo;
  data: {
    url: string;
    requestMethod?: HTTPMethod;
  };
  collection: {
    url: string;
    isLive?: boolean;
    params?: Params;
    insightData?: InsightData;
    onDataChanged?: (data) => Promise<void>;
    onError?: (errorInfo: ErrorInfo) => void;
    viewKey?: string;
  };
  upsellContext: UpsellContext;
  isBulkExport?: boolean;
};

export interface InsightData {
  content: InsightContent;
  paramsForData: Params;
  paramsForContext: Params;
  corePageSettings: Omit<CorePageProviderProps, 'children'>;
  componentSettings: Params;
  collection?: InsightCollection;
  dataType: InsightCollectionDataType;
  componentName?: string;
  proposition: Proposition;
  pageSettings?: PageSettings;
}

export interface InsightContent {
  type: string;
  dataOptions: DataOptions;
}

export interface InsightCollection
  extends Pick<InsightRendererProps<InsightData>, 'onLoaded' | 'onExportReady' | 'onError'> {}

export enum InsightCollectionDataType {
  live = 'live',
  static = 'static',
}

export enum ComponentHeaderActionType {
  Button = 'button',
  MenuItem = 'menuItem',
}
